/* eslint-disable @typescript-eslint/no-empty-function */
import { Directive, EventEmitter, Injector, Input, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { takeUntil } from 'rxjs';

import { DestroyableDirective } from '@shared/directive/destroyable.directive';

@Directive()
export class ControlValueAccessorBaseDirective<T = unknown> extends DestroyableDirective implements ControlValueAccessor, OnInit {
  @Input() class: string;
  @Input() placeholder: string;
  @Input() name: string;
  @Input() controlName: string;

  @Output() blurEvent = new EventEmitter();

  public control: AbstractControl | null = null;
  public innerControl: FormControl<T>;

  private injector = inject(Injector, { optional: true, self: true });

  ngOnInit(): void {
    this.getControl();
    this.initControlAndSubscribe();
  }

  public onBlur(event?: MouseEvent): void {
    this.onTouched();
    this.blurEvent.next(event);
  }

  public writeValue(value: T): void {
    this.innerControl.setValue(value);
  }

  public registerOnChange(fn: (value: T) => void): void {
    this.onChange = (value: T): void => {
      fn(value);
      this.innerControl.setValue(value, { emitEvent: false });
    };
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.innerControl.disable();
    } else {
      this.innerControl.enable();
    }
  }

  protected initControlAndSubscribe(): void {
    this.innerControl = new FormControl<T>(null, { updateOn: this.control?.updateOn ?? 'blur' });
    this.innerControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => this.onChange(value));
  }

  protected getControl(): void {
    const ngControl = this.injector.get(NgControl, null, { optional: true, self: true });
    if (ngControl) {
      ngControl.valueAccessor = this;
      this.control = ngControl.control;
    }
  }

  protected onChange: (value: T) => void = () => {};
  protected onTouched: () => void = () => {};
}
